// client/src/hooks/useBankSearch.js
import { useState, useMemo, useCallback } from 'react';
import { searchBanks, isValidBank, ITALIAN_BANKS } from '../components/utils/bankData';

/**
 * Hook personalizzato per la gestione della ricerca banche
 * @param {string} initialQuery - Query iniziale (default: '')
 * @param {number} maxResults - Numero massimo di risultati (default: 10)
 * @returns {object} Oggetto con stato e funzioni per la ricerca
 */
export const useBankSearch = (initialQuery = '', maxResults = 10) => {
  const [query, setQuery] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(false);

  // Memoizza i risultati filtrati per evitare re-calcoli inutili
  const filteredBanks = useMemo(() => {
    if (!query || query.length < 1) return [];
    
    setIsSearching(true);
    const results = searchBanks(query, maxResults);
    setIsSearching(false);
    
    return results;
  }, [query, maxResults]);

  // Memoizza la validazione della banca
  const isValidBankName = useMemo(() => {
    return isValidBank(query);
  }, [query]);

  // Funzione per resettare la ricerca
  const resetSearch = useCallback(() => {
    setQuery('');
  }, []);

  // Funzione per impostare una banca specifica
  const selectBank = useCallback((bankName) => {
    if (typeof bankName === 'string' && bankName.trim()) {
      setQuery(bankName.trim());
    }
  }, []);

  // Funzione per ottenere suggerimenti basati su input parziale
  const getSuggestions = useCallback((partialQuery, limit = 5) => {
    return searchBanks(partialQuery, limit);
  }, []);

  // Statistiche sulla ricerca corrente
  const searchStats = useMemo(() => {
    return {
      hasQuery: query.length > 0,
      hasResults: filteredBanks.length > 0,
      isValidSelection: isValidBankName,
      resultCount: filteredBanks.length,
      isExactMatch: filteredBanks.some(bank => 
        bank.toLowerCase() === query.toLowerCase()
      )
    };
  }, [query, filteredBanks, isValidBankName]);

  return {
    // Stato principale
    query,
    setQuery,
    filteredBanks,
    isValidBank: isValidBankName,
    isSearching,
    
    // Funzioni utili
    resetSearch,
    selectBank,
    getSuggestions,
    
    // Statistiche e info
    searchStats,
    totalBanks: ITALIAN_BANKS.length,
    
    // Per compatibilità con versioni precedenti
    allBanks: ITALIAN_BANKS
  };
};

/**
 * Hook semplificato per validazione banche
 * @param {string} bankName - Nome della banca da validare
 * @returns {object} Risultato della validazione
 */
export const useBankValidation = (bankName = '') => {
  const isValid = useMemo(() => isValidBank(bankName), [bankName]);
  const suggestions = useMemo(() => {
    if (!bankName || isValid) return [];
    return searchBanks(bankName, 3);
  }, [bankName, isValid]);

  return {
    isValid,
    suggestions,
    hasAlternatives: suggestions.length > 0
  };
};

/**
 * Hook per gestione state complesso con ricerca banche
 * Utile per form con multiple selezioni
 */
export const useBankForm = (initialBanks = []) => {
  const [selectedBanks, setSelectedBanks] = useState(initialBanks);
  const { query, setQuery, filteredBanks, resetSearch } = useBankSearch();

  const addBank = useCallback((bankName) => {
    if (isValidBank(bankName) && !selectedBanks.includes(bankName)) {
      setSelectedBanks(prev => [...prev, bankName]);
      resetSearch();
      return true;
    }
    return false;
  }, [selectedBanks, resetSearch]);

  const removeBank = useCallback((bankName) => {
    setSelectedBanks(prev => prev.filter(bank => bank !== bankName));
  }, []);

  const clearAll = useCallback(() => {
    setSelectedBanks([]);
    resetSearch();
  }, [resetSearch]);

  const isSelected = useCallback((bankName) => {
    return selectedBanks.includes(bankName);
  }, [selectedBanks]);

  return {
    // Ricerca
    query,
    setQuery,
    filteredBanks: filteredBanks.filter(bank => !isSelected(bank)),
    resetSearch,
    
    // Selezioni
    selectedBanks,
    addBank,
    removeBank,
    clearAll,
    isSelected,
    
    // Statistiche
    selectedCount: selectedBanks.length,
    canAddMore: true, // Sempre true per banche, ma configurabile
  };
};

export default useBankSearch;